import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ja';
import 'dayjs/locale/en';
import type { Locale } from '@/shared/lib/i18n';

// UTCモードとタイムゾーンを有効化
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 日付と時刻をlocaleに応じた形式でフォーマットする
 * @param date - yyyy-mm-dd 形式の日付文字列
 * @param time - HH:mm 形式の時刻文字列
 * @param locale - 言語設定 ('ja' | 'en')
 * @returns フォーマットされた日時文字列
 *
 * @example
 * formatDateTime('2025-01-15', '14:30', 'ja') // '2025/01/15 14:30'
 * formatDateTime('2025-01-15', '14:30', 'en') // 'Jan 15, 2025 2:30 PM'
 */
export const formatDateTime = (date: string, time: string, locale: Locale): string => {
    if (!date) return '';

    const dateTime = dayjs(`${date} ${time}`);

    if (locale === 'en') {
        return dateTime.locale('en').format('MMM D, YYYY h:mm A');
    }

    return dateTime.locale('ja').format('YYYY/MM/DD HH:mm');
};

/**
 * ISO 8601形式の日時文字列をJSTに変換して日付と時刻に分割する
 * @param isoDateTime - ISO 8601形式の日時文字列 (例: '2024-12-01T05:00:00Z')
 * @returns 日付 (yyyy-mm-dd) と時刻 (HH:mm) のタプル（JST）
 *
 * @example
 * splitIsoDateTime('2024-12-01T05:00:00Z') // ['2024-12-01', '14:00'] (UTC→JST変換)
 * splitIsoDateTime('') // ['', '']
 */
export const splitIsoDateTime = (isoDateTime: string): [string, string] => {
    if (!isoDateTime) return ['', ''];

    // UTCでパースし、JSTに変換
    const jstDateTime = dayjs.utc(isoDateTime).tz('Asia/Tokyo');
    const date = jstDateTime.format('YYYY-MM-DD');
    const time = jstDateTime.format('HH:mm');

    return [date, time];
};
